import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

const CashCollection = ({ open, onClose, onSubmit }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [cashCollection, setCashCollection] = useState({
    amount: '',
    officerName: '',
    notes: ''
  });
  const [confirmationCode, setConfirmationCode] = useState('');

  // For receipt confirmation code
  const generateConfirmationCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setConfirmationCode(code);
    return code;
  };

  const handleCashInputChange = (e) => {
    setCashCollection({
      ...cashCollection,
      [e.target.name]: e.target.value
    });
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      // Move to confirmation step
      generateConfirmationCode();
      setActiveStep(1);
    } else {
      // Complete the process
      onSubmit(cashCollection, confirmationCode);
      resetModal();
      onClose();
    }
  };

  const resetModal = () => {
    setActiveStep(0);
    setCashCollection({
      amount: '',
      officerName: '',
      notes: ''
    });
    setConfirmationCode('');
  };

  const isStepOneValid = () => {
    return cashCollection.amount > 0 && cashCollection.officerName.trim() !== '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Collect Cash Payment
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          <Step>
            <StepLabel>Enter Details</StepLabel>
          </Step>
          <Step>
            <StepLabel>Confirm Collection</StepLabel>
          </Step>
        </Stepper>
      </DialogTitle>

      <DialogContent>
        {activeStep === 0 ? (
          // Step 1: Enter cash collection details
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="amount"
                label="Amount Collected"
                type="number"
                fullWidth
                required
                value={cashCollection.amount}
                onChange={handleCashInputChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="officerName"
                label="Collection Officer Name"
                fullWidth
                required
                value={cashCollection.officerName}
                onChange={handleCashInputChange}
                placeholder="Enter the name of the officer who collected the payment"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Additional Notes"
                fullWidth
                multiline
                rows={2}
                value={cashCollection.notes}
                onChange={handleCashInputChange}
                placeholder="Add any relevant details about this collection"
              />
            </Grid>
          </Grid>
        ) : (
          // Step 2: Confirmation
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />

            <Typography variant="h5" gutterBottom>
              Confirm Cash Collection
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">Amount:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" fontWeight="bold">KES {cashCollection.amount}</Typography>
                </Grid>

                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">Collection Officer:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'left' }}>
                  <Typography variant="body1">{cashCollection.officerName}</Typography>
                </Grid>

                {cashCollection.notes && (
                  <>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary">Notes:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'left' }}>
                      <Typography variant="body1">{cashCollection.notes}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Receipt Confirmation Code
              </Typography>
              <Typography variant="h4" sx={{ letterSpacing: 2 }}>
                {confirmationCode}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please share this code with the member as proof of payment
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {activeStep === 0 ? (
          <>
            <Button onClick={() => {
              resetModal();
              onClose();
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNextStep}
              disabled={!isStepOneValid()}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleNextStep}
            >
              Complete Collection
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CashCollection;